import { PermissionEnum } from '@Application/Security/Roles';
import ActivateUser from '@Application/Users/Users/Commands/ActivateUser';
import RequestPasswordReset from '@Application/Users/Users/Commands/RequestPasswordReset';
import ResendInvitation from '@Application/Users/Users/Commands/ResendInvitation';
import RevokeInvitation from '@Application/Users/Users/Commands/RevokeInvitation';
import activateUser from '@Application/Users/Users/activateUser';
import requestPasswordReset from '@Application/Users/Users/requestPasswordReset';
import resendInvitation from '@Application/Users/Users/resendInvitation';
import revokeInvitation from '@Application/Users/Users/revokeInvitation';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { openUserSidebar } from '@UI/Pages/Administration/Users/Signals/userSidebarSignal';
import { setUserToDelete } from '@UI/Pages/Administration/Users/Signals/userToDeleteSignal';
import { setUserToDeactivate } from '@UI/Pages/Administration/Users/Signals/userToDeactivateSignal';
import { setUserToResetMfa } from '@UI/Pages/Administration/Users/Signals/userToResetMfaSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type UserView from '@UI/Store/Users/Views/UserView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, confirmDialog } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin-top: auto;
`;

type Props = {
	readonly user: UserView;
};

/**
 * Leaf action component: dispatches itself and opens overlays purely through their signals' setters
 * (no `onEdit`/`onDelete` callbacks). The action set is branched on the card's own state — invited,
 * deactivated, or active — and never renders the full set, letting a permission alone decide which of
 * them apply. Every action carries a visible label, rendered as `children` rather than `label`: the
 * kit resolves the accessible name from `label` first, which would shadow the explicit `aria-label`
 * naming the user (mirrors `Agencies/Facilities/FacilityCard/FacilityCardActions.tsx`).
 *
 * "Supprimer" (invited or never-activated deactivated card) opens its confirmation through
 * `userToDeleteSignal`; an activated account never offers it.
 *
 * "Annuler l'invitation" and "Réinitialiser le mot de passe" use the imperative `confirmDialog()`
 * host (mounted once, bare, at the page level): unlike deactivation and the MFA reset, no OTHER
 * component needs to know which user is being confirmed, so no signal is warranted for them.
 */
const UserCardActions: React.FC<Props> = ({ user }) => {
	const dispatch = useDispatch();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const accountId = currentUser.account?.id ?? null;

	const canWrite = useHasPermission(PermissionEnum.USER_WRITE);
	const canActivate = useHasPermission(PermissionEnum.USER_ACTIVATE);
	const isSelf = currentUser.id === user.id;

	const handleEditClick = React.useCallback(() => {
		openUserSidebar(user.id);
	}, [user.id]);

	const handleResendClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		dispatch(resendInvitation(new ResendInvitation(accountId, user.id))).catch(ignoreHandledError);
	}, [accountId, dispatch, user.id]);

	const handleRevokeClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		confirmDialog({
			header: "Annuler l'invitation",
			message: `Annuler l'invitation de ${user.fullName} ? Le lien reçu cessera de fonctionner.`,
			acceptLabel: "Annuler l'invitation",
			rejectLabel: 'Conserver',
			acceptSeverity: 'danger',
			accept: () => {
				dispatch(revokeInvitation(new RevokeInvitation(accountId, user.id))).catch(
					ignoreHandledError,
				);
			},
		});
	}, [accountId, dispatch, user.id, user.fullName]);

	const handlePasswordResetClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		confirmDialog({
			header: 'Réinitialiser le mot de passe',
			message: `Envoyer un lien de réinitialisation à ${user.email} ?`,
			accept: () => {
				dispatch(requestPasswordReset(new RequestPasswordReset(accountId, user.id))).catch(
					ignoreHandledError,
				);
			},
		});
	}, [accountId, dispatch, user.id, user.email]);

	const handleActivateClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		dispatch(activateUser(new ActivateUser(accountId, user.id))).catch(ignoreHandledError);
	}, [accountId, dispatch, user.id]);

	const handleDeactivateClick = React.useCallback(() => {
		setUserToDeactivate(user.id);
	}, [user.id]);

	const handleDeleteClick = React.useCallback(() => {
		setUserToDelete(user.id);
	}, [user.id]);

	const handleResetMfaClick = React.useCallback(() => {
		setUserToResetMfa(user.id);
	}, [user.id]);

	if (user.isPendingInvitation) {
		if (!canWrite) {
			return <div css={actionsRowStyle} />;
		}

		return (
			<div css={actionsRowStyle}>
				<Button
					type="button"
					size="sm"
					severity={'secondary'}
					aria-label={`Modifier ${user.fullName}`}
					onClick={handleEditClick}
				>
					Modifier
				</Button>
				<Button
					type="button"
					size="sm"
					aria-label={`Renvoyer l'invitation à ${user.fullName}`}
					severity={'secondary'}
					onClick={handleResendClick}
				>
					Renvoyer l'invitation
				</Button>
				<Button
					type="button"
					size="sm"
					severity={'secondary'}
					aria-label={`Annuler l'invitation de ${user.fullName}`}
					onClick={handleRevokeClick}
				>
					Annuler l'invitation
				</Button>
				<Button
					type="button"
					size="sm"
					severity={'danger'}
					aria-label={`Supprimer l'invitation de ${user.fullName}`}
					onClick={handleDeleteClick}
				>
					Supprimer
				</Button>
			</div>
		);
	}

	if (!user.isActive) {
		const canDelete = user.isNeverActivated && canWrite;

		if (!canActivate && !canDelete) {
			return <div css={actionsRowStyle} />;
		}

		return (
			<div css={actionsRowStyle}>
				{canActivate && (
					<Button
						type="button"
						size="sm"
						severity={'secondary'}
						aria-label={`Réactiver le compte de ${user.fullName}`}
						onClick={handleActivateClick}
					>
						Réactiver
					</Button>
				)}
				{canDelete && (
					<Button
						type="button"
						size="sm"
						severity={'danger'}
						aria-label={`Supprimer l'invitation de ${user.fullName}`}
						onClick={handleDeleteClick}
					>
						Supprimer
					</Button>
				)}
			</div>
		);
	}

	return (
		<div css={actionsRowStyle}>
			<WithPermission permission={PermissionEnum.USER_WRITE}>
				<Button
					type="button"
					size="sm"
					severity={'secondary'}
					aria-label={`Modifier ${user.fullName}`}
					onClick={handleEditClick}
				>
					Modifier
				</Button>
			</WithPermission>
			<WithPermission permission={PermissionEnum.USER_WRITE}>
				<Button
					type="button"
					size="sm"
					severity={'secondary'}
					aria-label={`Réinitialiser le mot de passe de ${user.fullName}`}
					onClick={handlePasswordResetClick}
				>
					Réinitialiser le mot de passe
				</Button>
			</WithPermission>
			<WithPermission permission={PermissionEnum.USER_MFA_RESET}>
				<Button
					type="button"
					size="sm"
					severity={'danger'}
					aria-label={`Réinitialiser le second facteur de ${user.fullName}`}
					onClick={handleResetMfaClick}
				>
					Réinitialiser le 2FA
				</Button>
			</WithPermission>
			<WithPermission permission={PermissionEnum.USER_DEACTIVATE}>
				{!isSelf && (
					<Button
						type="button"
						size="sm"
						aria-label={`Désactiver le compte de ${user.fullName}`}
						severity={'danger'}
						onClick={handleDeactivateClick}
					>
						Désactiver
					</Button>
				)}
			</WithPermission>
		</div>
	);
};

export default UserCardActions;
